import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Asterisk, Repeat } from 'lucide-react';
import type { AdfElementNode } from '@/hooks/useAdfStandard';

interface AdfElementDetailProps {
  element: AdfElementNode;
}

interface AttributeConfig {
  values?: string[];
  default?: string;
  description?: string;
}

export function AdfElementDetail({ element }: AdfElementDetailProps) {
  // Parse attributes JSON
  const attributes = element.attributes as Record<string, AttributeConfig> | null;
  const hasAttributes = attributes && Object.keys(attributes).length > 0;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <code className="text-xl md:text-2xl font-bold font-mono">
            &lt;{element.tag_name}&gt;
          </code>
          {element.is_required && (
            <Badge className="bg-green-500/10 text-green-600 border border-green-500/20">
              <Asterisk className="h-3 w-3 mr-1" />
              Required
            </Badge>
          )}
          {element.is_repeatable && (
            <Badge className="bg-blue-500/10 text-blue-600 border border-blue-500/20">
              <Repeat className="h-3 w-3 mr-1" />
              Repeatable
            </Badge>
          )}
        </div>
        <h1 className="text-lg text-muted-foreground">{element.display_name}</h1>
      </div>

      {/* Description */}
      {element.description && (
        <div className="prose prose-sm dark:prose-invert max-w-none">
          <p className="text-muted-foreground">{element.description}</p>
        </div>
      )}

      {/* Attributes Table */}
      {hasAttributes && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold">Attributes</h3>
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-1/4">Name</TableHead>
                  <TableHead className="w-1/3">Valid Values</TableHead>
                  <TableHead className="w-1/6">Default</TableHead>
                  <TableHead>Description</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Object.entries(attributes).map(([name, config]) => (
                  <TableRow key={name}>
                    <TableCell className="font-mono text-sm">{name}</TableCell>
                    <TableCell>
                      {config.values && config.values.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {config.values.map((v) => (
                            <Badge key={v} variant="secondary" className="text-xs font-mono">
                              {v}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">Any</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {config.default ? (
                        <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{config.default}</code>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {config.description || '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}
